import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";

// Extract Side type from Positioner.Props
export type Side = PopoverPrimitive.Positioner.Props["side"];

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 5,
  arrowPadding = 5,
  collisionPadding,
  collisionBoundary,
  sticky,
  anchor,
  positionerClassName,
  showArrow = true,
  children,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | "align"
    | "alignOffset"
    | "side"
    | "sideOffset"
    | "arrowPadding"
    | "collisionPadding"
    | "collisionBoundary"
    | "sticky"
    | "anchor"
  > & {
    showArrow?: boolean;
    positionerClassName?: string;
  }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        arrowPadding={arrowPadding}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary}
        sticky={sticky}
        anchor={anchor}
        className={cn("isolate z-50", positionerClassName)}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cnState(
            `bg-popover text-popover-foreground data-open:animate-in
            data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0
            data-closed:zoom-out-95 data-open:zoom-in-95
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 z-50 flex
            w-72 origin-(--transform-origin) flex-col gap-4 rounded-md p-4
            text-sm shadow-lg ring-1 outline-hidden duration-100
            has-[[data-slot=popover-arrow]]:shadow-lg
            blue:bg-white/10 blue:text-white blue:ring-white/20 blue:backdrop-blur-xl`,
            className,
          )}
          {...props}
        >
          {showArrow && <PopoverArrow />}
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-1 text-sm", className)}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cnState("font-medium", className)}
      {...props}
    />
  );
}

function PopoverDescription({ className, ...props }: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cnState("text-muted-foreground", className)}
      {...props}
    />
  );
}

function PopoverArrow({ className, ...props }: PopoverPrimitive.Arrow.Props) {
  return (
    <PopoverPrimitive.Arrow
      data-slot="popover-arrow"
      className={cnState(
        `fill-popover [&>svg]:fill-popover [&>svg]:stroke-border
        data-[side=bottom]:bottom-full data-[side=left]:left-full
        data-[side=right]:right-full data-[side=top]:top-full
        data-[side=bottom]:[&>svg]:rotate-180 data-[side=left]:[&>svg]:-rotate-90
        data-[side=right]:[&>svg]:rotate-90 data-[side=top]:[&>svg]:rotate-0`,
        className,
      )}
      {...props}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" className="block transition-transform">
        <path d="M0 0 L6 6 L12 0" className="fill-inherit stroke-inherit" />
      </svg>
    </PopoverPrimitive.Arrow>
  );
}

export {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverPrimitive,
  PopoverTitle,
  PopoverTrigger,
};
